// ViewTaskPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "./firebase.js";
import { onMessage, messaging } from "./firebase";
import { clearAppBadge } from "../utils/badge";
import { useNotification } from "../contexts/NotificationContext";

import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  arrayUnion,
  getDoc,
  getDocs,
  orderBy
} from "firebase/firestore";
// Removed Cloud Functions imports - using Node.js backend instead

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { Paper } from "@mui/material";

export default function ViewTaskPage({ embeddedProjectId, embeddedProjectName }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // Use props if provided (for embedded mode), otherwise use route params
  const projectId = embeddedProjectId || params.projectId;
  const projectName = embeddedProjectName || params.projectName;

  const [taskText, setTaskText] = useState("");
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // Global search term
  const [selectedIndex, setSelectedIndex] = useState(-1); // For keyboard navigation
  const [headerHeight, setHeaderHeight] = useState(0); // Header height for scrollable area
  const [isMobile, setIsMobile] = useState(false); // Check if mobile/tablet screen (max-width: 992px)
  const [userRole, setUserRole] = useState(""); // User role (admin/user)
  const [highlightedTaskId, setHighlightedTaskId] = useState(null); // Task to highlight from notification

  // Refs for task cards (for scrolling to selected item)
  const taskRefs = useRef({});
  const headerRef = useRef(null);
  const previousTasksRef = useRef([]); // Track previous tasks to detect new ones

  // modal state (edit)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState(null);

  // const STATUS_OPTIONS = ["Pending", "In Progress", "On Hold", "Completed"]; // unused
  // const [showAddBox, setShowAddBox] = useState(false); // unused
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(true); // Checkbox state - default TRUE (chat)
  const [assignedTo, setAssignedTo] = useState(""); // Assigned user UID for new tasks

  // @Mention feature state
  const [projectMembers, setProjectMembers] = useState([]); // List of project members for mentions
  const [showMentionDropdown, setShowMentionDropdown] = useState(false); // Control mention dropdown visibility
  const [mentionQuery, setMentionQuery] = useState(""); // Text typed after @ for filtering members
  const [mentionStartPos, setMentionStartPos] = useState(-1); // Position where @ was typed
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0); // Selected member index in dropdown
  const [mentionDropdownPosition, setMentionDropdownPosition] = useState({ top: 0, left: 0 }); // Position for dropdown
  const mentionDropdownRef = useRef(null); // Ref for mention dropdown container
  const mentionItemRefs = useRef({}); // Refs for individual mention items
  const textareaRef = useRef(null); // Ref for textarea
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // Control emoji picker visibility
  const [newChatIds, setNewChatIds] = useState([]); // Track new chat message IDs for highlighting
  const [mentionedCardIds, setMentionedCardIds] = useState([]); // Track cards where user is mentioned for yellow highlight
  const [quotedMessage, setQuotedMessage] = useState(null); // Track quoted message for reply
  // const tasksColPath = collection(db, "projects", projectId, "tasks");
  const tasksColPath = projectId
    ? collection(db, "projects", projectId, "tasks")
    : null;

  // Clear badge when ViewTaskPage is opened (user is viewing tasks)
  useEffect(() => {
    if (!projectId) return;

    const clearBadgeOnView = async () => {
      try {
        // Clear badge immediately when ViewTaskPage opens
        await clearAppBadge();
        console.log("✅ Badge cleared - user opened ViewTaskPage to view tasks");

        // Also clear after a small delay to ensure it stays cleared
        setTimeout(async () => {
          await clearAppBadge();
          console.log("✅ Badge cleared again (ensuring it stays cleared)");
        }, 500);
      } catch (error) {
        console.error("Error clearing badge on ViewTaskPage open:", error);
      }
    };

    // Clear badge immediately when page loads
    clearBadgeOnView();
  }, [projectId]);

  // Mark notifications as seen when user opens chat page (ViewTaskPage)
  // markSeen() should be called ONLY inside TaskViewPage useEffect
  useEffect(() => {
    if (!projectId) return;

    const markNotificationsAsSeen = async () => {
      try {
        const user = auth.currentUser;
        const currentUserUID = user?.uid;
        if (!currentUserUID) {
          console.log("No current user UID, skipping markNotificationsAsSeen");
          return;
        }

        const notificationsRef = collection(db, "notifications");
        // Get all notifications for this project
        const q = query(
          notificationsRef,
          where("projectId", "==", projectId)
        );

        const snapshot = await getDocs(q);
        console.log(`Found ${snapshot.docs.length} notifications for project ${projectId}`);

        // Update ALL notifications for this project - push currentUser UID to seenBy
        // Only update if not already in seenBy to avoid duplicates
        const notificationsToUpdate = snapshot.docs.filter((docSnap) => {
          const data = docSnap.data();
          const seenBy = data.seenBy || [];
          const shouldUpdate = !seenBy.includes(currentUserUID);
          if (shouldUpdate) {
            console.log(`Will update notification ${docSnap.id}, current seenBy:`, seenBy);
          }
          return shouldUpdate;
        });

        console.log(`Updating ${notificationsToUpdate.length} notifications for user ${currentUserUID}`);

        const updatePromises = notificationsToUpdate.map((docSnap) => {
          const docRef = doc(db, "notifications", docSnap.id);
          console.log(`Updating notification ${docSnap.id} with arrayUnion(${currentUserUID})`);
          return updateDoc(docRef, {
            seenBy: arrayUnion(currentUserUID), // Add current user UID to seenBy array
          });
        });

        if (updatePromises.length > 0) {
          await Promise.all(updatePromises);
          console.log(`Successfully updated ${updatePromises.length} notifications`);
        } else {
          console.log("No notifications to update");
        }

        // Always clear badge when user views tasks (notifications will be marked as seen)
        // Badge will be recalculated by notification count listener based on unread count
        // Since notifications are now marked as seen, unread count will be 0, so badge will stay cleared
        try {
          await clearAppBadge();
          console.log("✅ Badge cleared - user viewed tasks in this project");

          // Clear again after a delay to ensure it stays cleared
          // (in case notification count listener updates badge)
          setTimeout(async () => {
            await clearAppBadge();
            console.log("✅ Badge cleared again (ensuring it stays cleared after notifications marked as seen)");
          }, 1000);
        } catch (error) {
          console.error("Error clearing badge:", error);
        }
      } catch (error) {
        console.error("Error marking notifications as seen:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        // Error logged to console - user will see it in browser console
        console.error("Failed to mark notifications as seen:", error.message);
      }
    };

    markNotificationsAsSeen();
  }, [projectId]);

  // Fetch project members for @mention feature
  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!projectId) return;

      try {
        // Get project document to access users array
        const projectDoc = await getDoc(doc(db, "projects", projectId));

        if (projectDoc.exists()) {
          const projectData = projectDoc.data();
          const userIds = projectData.users || [];

          if (userIds.length > 0) {
            // Fetch user details for each user ID
            const usersRef = collection(db, "users");
            const usersSnapshot = await getDocs(usersRef);

            const members = [];
            usersSnapshot.forEach((userDoc) => {
              if (userIds.includes(userDoc.id)) {
                const userData = userDoc.data();
                members.push({
                  uid: userDoc.id,
                  name: userData.username || userData.email || "Unknown User",
                  email: userData.email || ""
                });
              }
            });

            setProjectMembers(members);
            console.log(`📋 Loaded ${members.length} project members for mentions:`, members);
          }
        }
      } catch (error) {
        console.error("Error fetching project members:", error);
      }
    };

    fetchProjectMembers();
  }, [projectId]);

  // Play notification sound function
  const playNotificationSound = () => {
    // Check if notifications are muted (admin only feature)
    const notificationsMuted = localStorage.getItem("notificationsMuted") === "true";
    if (notificationsMuted) {
      console.log("🔇 Notifications are muted, skipping sound");
      return; // Don't play sound if muted
    }

    try {
      // Create audio element for notification sound
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGW67+efTQ8MTqfj8LZjHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2Yk2CBhluu/nn00PDE6n4/C2YxwGOJHX8sx5LAUkd8fw3ZBAC');
      audio.volume = 0.7; // Set volume (0.0 to 1.0)
      audio.play().catch(err => {
        console.log("Could not play notification sound:", err);
        // Fallback: Use Web Audio API for simple beep
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          oscillator.frequency.value = 800; // Higher pitch like WhatsApp
          oscillator.type = 'sine';

          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);
        } catch (audioErr) {
          console.log("Could not play fallback sound:", audioErr);
        }
      });
    } catch (error) {
      console.log("Sound playback error:", error);
    }
  };

  // realtime listener
  useEffect(() => {
    if (!projectId || !tasksColPath) return;

    const q = query(tasksColPath, orderBy("createdAt", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const previousTasks = previousTasksRef.current;

        // Detect new tasks (not created by current user)
        if (previousTasks.length > 0 && arr.length > previousTasks.length) {
          const currentUser = auth.currentUser;
          const currentUserEmail = currentUser?.email;

          // Find new tasks
          const newTasks = arr.filter(newTask => {
            const exists = previousTasks.find(prevTask => prevTask.id === newTask.id);
            if (!exists) {
              // Only play sound if task was created by someone else
              const createdBy = newTask.createdBy || "";
              return createdBy !== currentUserEmail;
            }
            return false;
          });

          // Play sound for each new task from other users
          if (newTasks.length > 0) {
            console.log(`🔔 New task(s) detected: ${newTasks.length}`);
            playNotificationSound();

            // Mark notifications as seen for these tasks immediately
            const markTaskNotificationsAsSeen = async () => {
              try {
                const user = auth.currentUser;
                const currentUserUID = user?.uid;
                if (!currentUserUID) return;

                const notificationsRef = collection(db, "notifications");

                // Mark each new task's notification as seen
                for (const task of newTasks) {
                  const taskId = task.id;

                  // Find notification for this task
                  const q = query(
                    notificationsRef,
                    where("projectId", "==", projectId),
                    where("taskId", "==", taskId)
                  );

                  const snapshot = await getDocs(q);

                  // Update all notifications for this task
                  snapshot.docs.forEach(async (docSnap) => {
                    const notificationData = docSnap.data();
                    const seenBy = notificationData.seenBy || [];

                    // Only update if not already seen
                    if (!seenBy.includes(currentUserUID)) {
                      const docRef = doc(db, "notifications", docSnap.id);
                      await updateDoc(docRef, {
                        seenBy: arrayUnion(currentUserUID),
                      });
                      console.log(`✅ Marked notification ${docSnap.id} as seen for task ${taskId}`);
                    }
                  });
                }
              } catch (error) {
                console.error("Error marking task notifications as seen:", error);
              }
            };

            // Mark notifications as seen immediately
            markTaskNotificationsAsSeen();

            // Also show notification popup for each new task
            newTasks.forEach(task => {
              const createdBy = task.createdBy || "Unknown";
              const taskText = task.text || "New Task";

              showNotification({
                title: "New Task Added",
                body: `${createdBy} added new task: ${taskText}`,
                projectId: projectId,
                projectName: projectName || "",
                taskId: task.id,
                taskName: taskText,
                addedBy: task.createdBy || "",
                addedByName: task.createdBy || "",
                link: `/dashboard?projectId=${projectId}&taskId=${task.id}`,
                timestamp: Date.now(),
              });
            });
          }
        }

        // Update previous tasks reference
        previousTasksRef.current = arr;
        setTasks(arr);

        // Check for new chat messages to highlight (only chats created by others)
        if (previousTasks.length > 0 && arr.length > previousTasks.length) {
          const currentUser = auth.currentUser;
          const currentUserEmail = currentUser?.email;

          const newChats = arr.filter(newItem => {
            const exists = previousTasks.find(prev => prev.id === newItem.id);
            return !exists && newItem.type === "chat" && newItem.createdBy !== currentUserEmail;
          });

          if (newChats.length > 0) {
            setNewChatIds(prev => [...prev, ...newChats.map(chat => chat.id)]);
            console.log(`💬 ${newChats.length} new chat message(s) will be highlighted`);

            // Check if any new chats mention the current user
            const mentionedChats = newChats.filter(chat => isCurrentUserMentioned(chat.text));
            if (mentionedChats.length > 0) {
              const mentionedIds = mentionedChats.map(chat => chat.id);
              setMentionedCardIds(prev => [...prev, ...mentionedIds]);
              console.log(`🔔 You were mentioned in ${mentionedChats.length} message(s)!`);

              // Remove yellow highlight after 3 seconds
              setTimeout(() => {
                setMentionedCardIds(prev => prev.filter(id => !mentionedIds.includes(id)));
                console.log(`✅ Removed yellow highlight from mentioned cards`);
              }, 3000);
            }
          }
        }
        // Auto-scroll to latest message on initial load
        if (arr.length > 0 && previousTasks.length === 0) {
          setTimeout(() => {
            const lastTask = arr[arr.length - 1];
            const lastTaskElement = taskRefs.current[lastTask.id];
            if (lastTaskElement) {
              lastTaskElement.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
              console.log("📜 Auto-scrolled to latest message");
            }
          }, 500);
        }
      },
      (err) => {
        console.error("Tasks snapshot error:", err);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectName, showNotification]);

  // Handle foreground FCM messages (when app is open)
  useEffect(() => {
    if (!messaging || !onMessage) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      // Only handle notifications for current project
      const payloadProjectId = payload.data?.projectId || "";
      if (payloadProjectId !== projectId) return;

      const addedBy = payload.data?.addedByName || payload.data?.addedBy || payload.data?.createdBy || "Unknown";
      const taskName = payload.data?.taskName || "New Task";
      const taskId = payload.data?.taskId || "";

      // Mark notification as seen immediately since user is viewing the page
      const markNotificationAsSeen = async () => {
        try {
          const user = auth.currentUser;
          const currentUserUID = user?.uid;
          if (!currentUserUID || !taskId) return;

          const notificationsRef = collection(db, "notifications");

          // Find notification for this task
          const q = query(
            notificationsRef,
            where("projectId", "==", projectId),
            where("taskId", "==", taskId)
          );

          const snapshot = await getDocs(q);

          // Update all notifications for this task
          snapshot.docs.forEach(async (docSnap) => {
            const notificationData = docSnap.data();
            const seenBy = notificationData.seenBy || [];

            // Only update if not already seen
            if (!seenBy.includes(currentUserUID)) {
              const docRef = doc(db, "notifications", docSnap.id);
              await updateDoc(docRef, {
                seenBy: arrayUnion(currentUserUID),
              });
              console.log(`✅ Marked FCM notification ${docSnap.id} as seen for task ${taskId}`);
            }
          });
        } catch (error) {
          console.error("Error marking FCM notification as seen:", error);
        }
      };

      // Mark as seen immediately
      markNotificationAsSeen();

      // Play sound
      playNotificationSound();

      // Show notification popup
      const notificationLink = taskId
        ? `/dashboard?projectId=${projectId}&taskId=${taskId}`
        : (payload.data?.link || `/dashboard?projectId=${projectId}`);

      showNotification({
        title: "New Task Added",
        body: `${addedBy} added new task: ${taskName}`,
        projectId: payloadProjectId,
        projectName: payload.data?.projectName || projectName || "",
        taskId: taskId,
        taskName: taskName,
        addedBy: payload.data?.addedBy || payload.data?.createdBy || "",
        addedByName: addedBy,
        link: notificationLink,
        timestamp: payload.data?.timestamp || Date.now(),
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [projectId, projectName, showNotification]);

  // Handle taskId from URL (from notification click)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const taskIdFromUrl = searchParams.get('taskId');

    if (taskIdFromUrl && tasks.length > 0) {
      // Find the task
      const task = tasks.find(t => t.id === taskIdFromUrl);
      if (task && taskRefs.current[taskIdFromUrl]) {
        // Scroll to and highlight the task
        setTimeout(() => {
          taskRefs.current[taskIdFromUrl].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          setHighlightedTaskId(taskIdFromUrl);

          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedTaskId(null);
            // Remove taskId from URL
            const newSearchParams = new URLSearchParams(location.search);
            newSearchParams.delete('taskId');
            navigate(`${location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`, { replace: true });
          }, 3000);
        }, 500); // Small delay to ensure DOM is ready
      }
    }
  }, [location.search, tasks, navigate, location.pathname]);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setUserRole("user");
          return;
        }

        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          setUserRole(userDocSnap.data().role || "user");
        } else {
          setUserRole("user");
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        setUserRole("user");
      }
    };

    fetchUserRole();
  }, []);

  // Helper function to get current user's email
  const getCurrentUserEmail = () => {
    const user = auth.currentUser;
    return user?.email || null;
  };

  // Helper function to get current user's UID
  const getCurrentUserUID = () => {
    const user = auth.currentUser;
    return user?.uid || null;
  };

  // Helper function to capitalize first letter of name
  const capitalizeName = (name) => {
    if (!name || typeof name !== 'string') return name || "Unknown";
    // Split by space to handle multiple words (e.g., "john doe" -> "John Doe")
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Update task assignment
  const updateTaskAssignment = async (taskId, assignedToUID) => {
    if (!taskId) return;

    try {
      const taskRef = doc(db, "projects", projectId, "tasks", taskId);
      const updateData = {
        updatedAt: serverTimestamp(),
      };

      if (assignedToUID) {
        updateData.assignedTo = assignedToUID;
        // Also store assigned user name and email for easy display
        const assignedMember = projectMembers.find(m => m.uid === assignedToUID);
        if (assignedMember) {
          updateData.assignedToName = assignedMember.name;
          updateData.assignedToEmail = assignedMember.email;
        }
      } else {
        // Remove assignment
        updateData.assignedTo = null;
        updateData.assignedToName = null;
        updateData.assignedToEmail = null;
      }

      await updateDoc(taskRef, updateData);
    } catch (error) {
      console.error("Error updating task assignment:", error);
      alert(`Failed to update assignment: ${error.message || "Unknown error"}`);
    }
  };

  // Handle quote reply
  const handleQuoteReply = (message) => {
    setQuotedMessage({
      id: message.id,
      text: message.text || "",
      createdBy: message.createdBy || "",
      createdByName: message.createdByName || "Unknown"
    });
    // Scroll to input area
    setTimeout(() => {
      const inputElement = document.querySelector('.whatsapp-input-container textarea, .whatsapp-input-container input[type="text"]');
      if (inputElement) {
        inputElement.focus();
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  // Handle thumbs up reaction
  const handleThumbsUpReaction = async (taskId, currentReactions = []) => {
    if (!taskId || !projectId) return;

    try {
      const user = auth.currentUser;
      if (!user) {
        alert("You must be logged in to react.");
        return;
      }

      const userEmail = user.email || "";
      const userName = capitalizeName(user?.displayName?.trim() || userEmail?.split("@")[0] || "User");

      // Check if user already reacted
      const existingReaction = currentReactions.find(
        (r) => r.email === userEmail
      );

      const taskRef = doc(db, "projects", projectId, "tasks", taskId);

      if (existingReaction) {
        // Remove reaction - filter out this user's reaction
        const updatedReactions = currentReactions.filter(
          (r) => r.email !== userEmail
        );
        await updateDoc(taskRef, {
          thumbsUpReactions: updatedReactions,
          updatedAt: serverTimestamp()
        });
      } else {
        // Add reaction - create new array with the new reaction
        // Note: serverTimestamp() cannot be used inside arrays, so we use a regular timestamp
        const reactionData = {
          email: userEmail,
          name: userName,
          reactedAt: new Date().toISOString() // Use ISO string timestamp instead of serverTimestamp()
        };
        const updatedReactions = [...currentReactions, reactionData];
        await updateDoc(taskRef, {
          thumbsUpReactions: updatedReactions,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error("Error toggling thumbs up reaction:", error);
      alert(`Failed to update reaction: ${error.message || "Unknown error"}`);
    }
  };

  // Update task status - anyone can update
  const updateTaskStatus = async (taskId, newStatus) => {
    if (!taskId || !newStatus) return;

    try {
      const user = auth.currentUser;
      const updatedBy = user?.displayName?.trim() || user?.email || "Unknown";

      const taskRef = doc(db, "projects", projectId, "tasks", taskId);
      await updateDoc(taskRef, {
        status: newStatus,
        updatedBy: updatedBy,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating task status:", error);
      alert(`Failed to update status: ${error.message || "Unknown error"}`);
    }
  };

  // Helper function to check if current user is the task creator
  const isTaskCreator = (task) => {
    const currentUserEmail = getCurrentUserEmail();
    if (!currentUserEmail || !task.createdBy) return false;
    return task.createdBy === currentUserEmail;
  };

  // Add task
  const addTask = async () => {
    const textToSend = taskText.trim();
    if (!textToSend) return;

    // Clear input immediately for better UX
    setTaskText("");
    setAssignedTo("");
    setQuotedMessage(null); // Clear quoted message after sending

    // Reset textarea height
    setTimeout(() => {
      const textarea = document.querySelector('.whatsapp-input-field');
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = "40px"; // Reset to min height
      }
    }, 0);

    const user = auth.currentUser;
    if (!user) {
      alert("You must be logged in to create tasks.");
      setTaskText(textToSend); // Restore text if failed
      return;
    }

    const userEmail = user.email || "Guest";
    const userName = user?.displayName?.trim() || userEmail;

    // Determine type based on checkbox state
    const itemType = isCheckboxChecked ? "chat" : "task";

    // Create base object with common fields
    const itemData = {
      text: textToSend,
      type: itemType, // "task" or "chat"
      createdBy: userEmail, // Store email of creator (never changes)
      createdByName: userName, // Store name of creator (never changes)
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      projectId: projectId,   // <-- REQUIRED !!
    };

    // Add quoted message if replying
    if (quotedMessage) {
      itemData.quotedMessage = {
        id: quotedMessage.id,
        text: quotedMessage.text,
        createdBy: quotedMessage.createdBy,
        createdByName: quotedMessage.createdByName
      };
      setQuotedMessage(null); // Clear quoted message after use
    }

    // Add task-specific fields only if it's a task
    if (itemType === "task") {
      itemData.status = "Pending";
      itemData.updatedBy = userName; // Store name of last updater (changes only on status update)
      // Add assignedTo if selected
      if (assignedTo) {
        itemData.assignedTo = assignedTo; // Store UID of assigned user
        // Also store assigned user name for easy display
        const assignedMember = projectMembers.find(m => m.uid === assignedTo);
        if (assignedMember) {
          itemData.assignedToName = assignedMember.name;
          itemData.assignedToEmail = assignedMember.email;
        }
      }
    }

    try {
      const taskDocRef = await addDoc(tasksColPath, itemData);

      // OPTIMIZED: Create ONE notification per task (not per user)
      // Previously: Created one notification per project member (inefficient)
      // Now: Single notification shared by all project members, tracked via seenBy array
      // This reduces Firestore documents by ~80-90% while maintaining same functionality
      //
      // ENHANCED: For CHATS with @mentions, send targeted notifications to mentioned users

      const userUID = user.uid;
      const notificationsRef = collection(db, "notifications");

      // Check if this is a chat with @mentions
      const mentionedUsers = itemType === "chat" ? extractMentions(textToSend) : [];

      if (mentionedUsers.length > 0) {
        // CHAT WITH MENTIONS: Send targeted notifications to each mentioned user
        console.log(`📝 Creating ${mentionedUsers.length} mention notification(s) for chat`);
        for (const mentionedUser of mentionedUsers) {
          // Only send notification if mentioned user is not the creator
          if (mentionedUser.uid !== userUID) {
            const mentionNotificationData = {
              projectId: projectId,
              projectName: projectName || "Project",
              taskId: taskDocRef.id,
              taskName: textToSend,
              createdBy: userEmail,
              createdByUID: userUID,
              createdByName: userName,
              time: serverTimestamp(),
              seenBy: [userUID], // Creator already added
              mentionedUser: mentionedUser.uid, // Mark this as a mention notification
              mentionType: "chat", // Type of mention
            };

            console.log(`📝 Creating mention notification:`, {
              projectId: mentionNotificationData.projectId,
              taskId: mentionNotificationData.taskId,
              mentionedUser: mentionNotificationData.mentionedUser,
              seenBy: mentionNotificationData.seenBy
            });

            const mentionDocRef = await addDoc(notificationsRef, mentionNotificationData);
            console.log(`✅ Mention notification created successfully with ID: ${mentionDocRef.id} for user ${mentionedUser.uid}`);
          } else {
            console.log(`⏭️ Skipping mention notification for creator (${userUID})`);
          }
        }
      } else {
        // REGULAR TASK or CHAT WITHOUT MENTIONS: Create general notification for all project members
        // Creator is added to seenBy initially so they never see badge for their own task
        // All other project members will see this notification until they mark it as seen
        // Badge count logic filters by seenBy array to show unread count per user
        const notificationData = {
          projectId: projectId,
          projectName: projectName || "Project",
          taskId: taskDocRef.id,
          taskName: textToSend,
          createdBy: userEmail,
          createdByUID: userUID, // Store UID for filtering
          createdByName: userName,
          time: serverTimestamp(),
          seenBy: [userUID], // Creator UID added initially - creator never sees badge for own task
          // When other users open ViewTaskPage, their UID is added to seenBy array
          // Badge count in Dashboard filters notifications where seenBy doesn't include current user
          // Since creator is already in seenBy, they won't see the badge
        };

        console.log(`📝 Creating notification for ${itemType} (no mentions):`, {
          projectId: notificationData.projectId,
          taskId: notificationData.taskId,
          seenBy: notificationData.seenBy,
          createdBy: notificationData.createdBy
        });

        const notificationDocRef = await addDoc(notificationsRef, notificationData);
        console.log(`✅ Notification created successfully with ID: ${notificationDocRef.id}`);
      }

      // Send notification via backend
      const API_URL = process.env.REACT_APP_NOTIFICATION_API_URL ||
        (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
          ? "http://localhost:3001"
          : "https://todo-server-xvr8.onrender.com");

      // Don't await this to avoid blocking UI if backend is slow
      fetch(`${API_URL}/notify-task`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectId: projectId,
          addedBy: userEmail,
          addedByName: userName,
          taskName: textToSend,
          taskId: taskDocRef.id,
          origin: window.location.origin,
        }),
      }).catch(err => {
        console.error("Notification send failed:", err);
      });
    } catch (error) {
      console.error("Error creating task/notification:", error);
    }
  };


  // Open edit modal (we keep modal behavior as original)
  const openEditModal = (task) => {
    // Anyone assigned to the project can edit tasks now
    // (they can change status, but only creator can change text)
    setEditingTask({
      ...task,
      text: task.text ?? "",
      status: task.status ?? "Pending",
      updatedBy: task.updatedBy ?? (auth.currentUser?.displayName ?? "User"),
      isCreator: isTaskCreator(task) // Store whether user is creator
    });
    setShowEditModal(true);
  };

  // Save editing changes
  const saveEdit = async () => {
    if (!editingTask?.id) return;

    const user = auth.currentUser;
    const by = user?.displayName?.trim() || user?.email || "Guest";

    // Get the original task to check what changed
    const originalTask = tasks.find(t => t.id === editingTask.id);

    // Check if text was changed
    const textChanged = originalTask && originalTask.text !== editingTask.text;

    // Only creator can change text
    if (textChanged && !isTaskCreator(originalTask)) {
      alert("You don't have permission to edit the task text. Only the creator can edit text.");
      setShowEditModal(false);
      setEditingTask(null);
      return;
    }

    const ref = doc(db, "projects", projectId, "tasks", editingTask.id);

    // Prepare update object based on item type
    const updateData = {
      updatedAt: serverTimestamp(),
    };

    // Add text to update only if it changed
    if (textChanged) {
      updateData.text = editingTask.text;
    }

    // Only add status-related fields for TASKS (not chats)
    if (editingTask.type === "task" || !editingTask.type) {
      const statusChanged = originalTask && originalTask.status !== editingTask.status;

      // Always include status in update
      updateData.status = editingTask.status;

      // Always update updatedBy when status changes
      // This ensures we track who last updated the status
      if (statusChanged) {
        updateData.updatedBy = by;
      }
    }

    // CRITICAL: createdBy and createdByName are NEVER updated - they stay the same forever
    // These fields are not included in updateData to ensure they never change
    await updateDoc(ref, updateData);
    setShowEditModal(false);
    setEditingTask(null);
  };

  // Delete
  const confirmDelete = (taskId) => {
    setDeletingTaskId(taskId);
    setShowDeleteConfirm(true);
  };

  const doDelete = async () => {
    if (!deletingTaskId) return;

    // Check if user is the creator
    const task = tasks.find(t => t.id === deletingTaskId);
    if (task && !isTaskCreator(task)) {
      alert("You don't have permission to delete this task. Only the creator can delete.");
      setShowDeleteConfirm(false);
      setDeletingTaskId(null);
      return;
    }

    await deleteDoc(doc(db, "projects", projectId, "tasks", deletingTaskId));
    setShowDeleteConfirm(false);
    setDeletingTaskId(null);
  };

  // Extract @mentions from text and return mentioned users
  const extractMentions = (text) => {
    if (!text || !projectMembers.length) return [];

    // Match @Name patterns in the text
    const mentionPattern = /@(\S+)/g;
    const matches = text.matchAll(mentionPattern);
    const mentioned = [];

    for (const match of matches) {
      const mentionedName = match[1];
      // Find the member by name (case-insensitive)
      const member = projectMembers.find(m =>
        m.name.toLowerCase().includes(mentionedName.toLowerCase())
      );
      if (member && !mentioned.find(m => m.uid === member.uid)) {
        mentioned.push(member);
      }
    }

    return mentioned;
  };

  // Calculate position for mention dropdown next to @ symbol
  const calculateMentionDropdownPosition = (textarea, atPosition) => {
    if (!textarea) return;

    try {
      // Create a temporary span to measure text position more accurately
      const textBeforeAt = taskText.substring(0, atPosition);
      const textareaStyle = window.getComputedStyle(textarea);
      const font = `${textareaStyle.fontSize} ${textareaStyle.fontFamily}`;

      // Create a temporary canvas to measure text width
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      context.font = font;
      const textWidth = context.measureText(textBeforeAt).width;

      // Get textarea position and dimensions
      const textareaRect = textarea.getBoundingClientRect();

      // Calculate line height
      const lineHeight = parseFloat(textareaStyle.lineHeight) || parseFloat(textareaStyle.fontSize) * 1.2;

      // Find which line the @ is on by counting newlines
      const textUpToAt = taskText.substring(0, atPosition);
      const lineNumber = (textUpToAt.match(/\n/g) || []).length;

      // Calculate vertical position (top of current line)
      // Account for textarea scroll
      const lineTop = lineNumber * lineHeight - textarea.scrollTop;

      // Account for padding
      const paddingLeft = parseFloat(textareaStyle.paddingLeft) || 0;
      const paddingTop = parseFloat(textareaStyle.paddingTop) || 0;

      // Calculate position relative to viewport (fixed positioning)
      // Account for textarea scroll
      const left = textareaRect.left + paddingLeft + textWidth - textarea.scrollLeft;
      const lineTopAbsolute = textareaRect.top + paddingTop + lineTop;

      // Position dropdown just above the @ symbol
      // Since we use translateY(-100%), the dropdown will move up by its height
      // So we position it at the @ line with a small gap
      const gapAbove = 4; // Small gap between @ and dropdown bottom
      const top = lineTopAbsolute - gapAbove; // Position just above the @ line

      // Ensure dropdown doesn't go off screen
      const dropdownWidth = 240; // minWidth from dropdown style
      const viewportWidth = window.innerWidth;
      const maxDropdownHeight = 280; // maxHeight from dropdown style

      // Adjust horizontal position if it would overflow
      const adjustedLeft = left + dropdownWidth > viewportWidth
        ? viewportWidth - dropdownWidth - 10
        : left;

      // Check if dropdown would go above viewport (accounting for translateY(-100%))
      // The dropdown extends from (top - dropdownHeight) to top
      const dropdownTopEdge = top - maxDropdownHeight;

      // Adjust vertical position if it would go above viewport
      const adjustedTop = dropdownTopEdge < 10
        ? lineTopAbsolute + lineHeight + gapAbove // Show below if no room above
        : top;

      setMentionDropdownPosition({ top: adjustedTop, left: adjustedLeft });
    } catch (error) {
      console.error('Error calculating mention dropdown position:', error);
      // Fallback: position at textarea
      const textareaRect = textarea.getBoundingClientRect();
      setMentionDropdownPosition({
        top: textareaRect.top - 200,
        left: textareaRect.left
      });
    }
  };

  // Insert mention (@name) into task text
  const insertMention = (memberName, replaceQuery = true) => {
    if (mentionStartPos === -1) {
      // Fallback: just append if no position tracked
      const mention = `@${memberName} `;
      setTaskText(prevText => prevText + mention);
    } else {
      // Replace @query with @memberName
      const beforeMention = taskText.substring(0, mentionStartPos);
      const afterMention = taskText.substring(mentionStartPos + 1 + mentionQuery.length);
      const mention = `@${memberName} `;
      setTaskText(beforeMention + mention + afterMention);
    }
    setShowMentionDropdown(false);
    setMentionQuery("");
    setMentionStartPos(-1);
    setSelectedMentionIndex(0);
  };

  // Filter project members based on mention query
  const getFilteredMembers = () => {
    if (!mentionQuery.trim()) {
      return projectMembers;
    }
    const query = mentionQuery.toLowerCase();
    return projectMembers.filter(member =>
      member.name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query)
    );
  };

  // Reset selected index when filtered members change
  useEffect(() => {
    if (showMentionDropdown) {
      let filtered;
      if (!mentionQuery.trim()) {
        filtered = projectMembers;
      } else {
        const query = mentionQuery.toLowerCase();
        filtered = projectMembers.filter(member =>
          member.name.toLowerCase().includes(query) ||
          member.email.toLowerCase().includes(query)
        );
      }
      setSelectedMentionIndex(prev => {
        if (prev >= filtered.length && filtered.length > 0) {
          return 0;
        }
        return prev;
      });
    }
  }, [mentionQuery, projectMembers, showMentionDropdown]);

  // Insert emoji into task text
  const insertEmoji = (emoji) => {
    setTaskText(prevText => prevText + emoji);
    // Keep emoji picker open for quick selection
  };

  // Common emojis for quick access (WhatsApp-style minimal set)
  const commonEmojis = [
    "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇",
    "🙂", "🙃", "😉", "😌", "😍", "🥰", "😘", "😗", "😙", "😚",
    "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩",
    "🥳", "😏", "😒", "😞", "😔", "😟", "😕", "🙁", "😣", "😖",
    "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡", "🤬", "🤯",
    "😳", "🥵", "🥶", "😱", "😨", "😰", "😥", "😓", "🤗", "🤔",
    "🤭", "🤫", "🤥", "😶", "😐", "😑", "😬", "🙄", "😯", "😦",
    "😧", "😮", "😲", "🥱", "😴", "🤤", "😪", "😵", "🤐", "🥴",
    "🤢", "🤮", "🤧", "😷", "🤒", "🤕", "🤑", "🤠", "😈", "👿",
    "👹", "👺", "🤡", "💩", "👻", "💀", "☠️", "👽", "👾", "🤖",
    "👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👋", "🤚",
    "🖐", "✋", "🖖", "👏", "🙌", "🤲", "🤝", "🙏", "✍️", "💪",
    "🦵", "🦶", "👂", "👃", "🧠", "🦷", "🦴", "👀", "👁", "👅",
    "👄", "💋", "💘", "💝", "💖", "💗", "💓", "💞", "💕", "💟",
    "❣️", "💔", "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍",
    "🤎", "💯", "💢", "💥", "💫", "💦", "💨", "🕳️", "💣", "💬",
    "👁️‍🗨️", "🗨️", "🗯️", "💭", "💤", "👋", "🤚", "🖐", "✋", "🖖",
    "👏", "🙌", "🤲", "🤝", "🙏", "✍️", "💪", "🦵", "🦶", "👂"
  ];

  // Helper to extract clean URL and trailing punctuation
  const extractUrlAndPunctuation = (raw) => {
    let clean = raw;
    let trailing = "";
    while (clean.length > 0) {
      const lastChar = clean[clean.length - 1];
      if ([".", ",", ";", ":", "!", "?"].includes(lastChar)) {
        trailing = lastChar + trailing;
        clean = clean.slice(0, -1);
      } else if (lastChar === ")" && (clean.match(/\(/g) || []).length < (clean.match(/\)/g) || []).length) {
        trailing = lastChar + trailing;
        clean = clean.slice(0, -1);
      } else if (lastChar === "]" && (clean.match(/\[/g) || []).length < (clean.match(/\]/g) || []).length) {
        trailing = lastChar + trailing;
        clean = clean.slice(0, -1);
      } else {
        break;
      }
    }
    return { cleanUrl: clean, trailing };
  };

  // Helper to render genuine URLs: Google Meet links as dedicated "Join Meeting" button, and regular web links as clickable hyperlinks
  const renderLinkElement = (rawPart, index) => {
    const { cleanUrl, trailing } = extractUrlAndPunctuation(rawPart);
    if (!cleanUrl) {
      return <span key={index}>{rawPart}</span>;
    }

    let url = cleanUrl;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    // Google Meet links retain the dedicated "Join Meeting" quick button as originally designed
    if (url.includes('meet.google.com')) {
      return (
        <React.Fragment key={index}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
            style={{
              backgroundColor: '#fca5a5',
              color: '#991b1b',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              margin: '4px 2px',
              display: 'inline-block',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f87171';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fca5a5';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
            }}
            title={url}
          >
            Join Meeting
          </button>
          {trailing}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={index}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: "#0284c7",
            textDecoration: "underline",
            wordBreak: "break-all",
            fontWeight: 500,
            cursor: "pointer"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#0369a1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#0284c7";
          }}
        >
          {cleanUrl}
        </a>
        {trailing}
      </React.Fragment>
    );
  };

  // Pattern for genuine web links (http, https, www, or meet.google.com)
  const genuineUrlPattern = /^(https?:\/\/[^\s]+|www\.[a-zA-Z0-9-]+\.[^\s]+|meet\.google\.com\/[^\s]+)$/i;

  // Render chat text with @mentions highlighted and genuine links clickable without buttons
  const renderChatText = (text) => {
    if (!text) return text;
    if (!projectMembers.length) {
      // If no project members, still convert genuine URLs to clickable links
      return convertUrlsToLinks(text);
    }

    // Get current user's email to check if mention is for current user
    const currentUser = auth.currentUser;
    const currentUserEmail = currentUser?.email;

    // Split text by @ mentions and genuine URLs only
    const parts = text.split(/(@\S+|https?:\/\/[^\s]+|www\.[a-zA-Z0-9-]+\.[^\s]+|meet\.google\.com\/[^\s]+)/gi);

    return parts.map((part, index) => {
      // Check if this part is a mention (starts with @)
      if (part.startsWith('@')) {
        const mentionedText = part.substring(1); // Remove @

        // Try to find member by exact email match first, then by name
        let member = projectMembers.find(m =>
          m.email?.toLowerCase() === mentionedText.toLowerCase()
        );

        // If not found by email, try by name
        if (!member) {
          member = projectMembers.find(m =>
            m.name?.toLowerCase().includes(mentionedText.toLowerCase())
          );
        }

        if (member) {
          // Check if this mention is for the current user
          const isCurrentUser = currentUserEmail && member.email === currentUserEmail;

          // Green for current user, blue for others
          const mentionColor = isCurrentUser ? '#28a745' : '#007bff';
          const mentionBgColor = isCurrentUser ? 'rgba(40, 167, 69, 0.1)' : 'rgba(0, 123, 255, 0.1)';

          return (
            <span
              key={index}
              style={{
                color: mentionColor,
                fontWeight: '600',
                backgroundColor: mentionBgColor,
                padding: '2px 6px',
                borderRadius: '4px'
              }}
            >
              @{capitalizeName(member.name)}
            </span>
          );
        }
      }

      // Check if this part is a genuine URL
      if (genuineUrlPattern.test(part)) {
        return renderLinkElement(part, index);
      }

      return <span key={index}>{part}</span>;
    });
  };

  // Helper function to convert genuine URLs to clickable links (used when no project members)
  const convertUrlsToLinks = (text) => {
    if (!text) return text;

    const parts = text.split(/(https?:\/\/[^\s]+|www\.[a-zA-Z0-9-]+\.[^\s]+|meet\.google\.com\/[^\s]+)/gi);

    return parts.map((part, index) => {
      if (genuineUrlPattern.test(part)) {
        return renderLinkElement(part, index);
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Check if current user is mentioned in the text
  const isCurrentUserMentioned = (text) => {
    if (!text || !projectMembers.length) return false;

    const currentUser = auth.currentUser;
    const currentUserEmail = currentUser?.email;
    if (!currentUserEmail) return false;

    const currentUserData = projectMembers.find(m => m.email === currentUserEmail);
    if (!currentUserData) return false;

    // Check if text contains @username or @email matching current user
    const mentionPattern = /@(\S+)/g;
    const matches = text.matchAll(mentionPattern);

    for (const match of matches) {
      const mentionedName = match[1];

      // Check if mention matches current user's name or email
      if (currentUserData.name.toLowerCase().includes(mentionedName.toLowerCase()) ||
        currentUserData.email.toLowerCase().includes(mentionedName.toLowerCase())) {
        return true;
      }
    }

    return false;
  };

  // helper: format Firestore timestamp safely
  const formatTs = (ts) => {
    if (!ts) return "—";

    let date;

    if (ts.seconds) {
      date = new Date(ts.seconds * 1000);
    } else {
      date = new Date(ts);
    }

    if (isNaN(date)) return "—";

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" }); // Nov
    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${day} ${month}, ${time}`;
  };

  // Helper function to calculate task age in days
  const calculateTaskAge = (createdAt) => {
    if (!createdAt) return null;

    let createdDate;

    if (createdAt.seconds) {
      createdDate = new Date(createdAt.seconds * 1000);
    } else {
      createdDate = new Date(createdAt);
    }

    if (isNaN(createdDate)) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    createdDate.setHours(0, 0, 0, 0);

    const diffTime = today - createdDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day";
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} year${years !== 1 ? 's' : ''}`;
  };

  // Helper function to highlight search text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '2px 0', borderRadius: '2px' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Filter tasks based on search term
  const filteredTasks = searchTerm.trim()
    ? tasks.filter((t) =>
      t.text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.updatedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.status?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : tasks;

  // Get matching task indices for keyboard navigation
  const matchingTaskIndices = tasks
    .map((t, index) => {
      if (!searchTerm.trim()) return -1;
      const matches =
        t.text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.updatedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.status?.toLowerCase().includes(searchTerm.toLowerCase());
      return matches ? index : -1;
    })
    .filter(index => index !== -1);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!searchTerm.trim() || matchingTaskIndices.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev < matchingTaskIndices.length - 1 ? prev + 1 : 0;
          // Scroll to the selected task
          const taskId = tasks[matchingTaskIndices[next]]?.id;
          if (taskId && taskRefs.current[taskId]) {
            taskRefs.current[taskId].scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : matchingTaskIndices.length - 1;
          // Scroll to the selected task
          const taskId = tasks[matchingTaskIndices[next]]?.id;
          if (taskId && taskRefs.current[taskId]) {
            taskRefs.current[taskId].scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return next;
        });
      } else if (e.key === 'Escape') {
        setSearchTerm("");
        setSelectedIndex(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchTerm, matchingTaskIndices, tasks]);

  // Reset selected index when search term changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchTerm]);

  // Close emoji picker and mention dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker || showMentionDropdown) {
        const target = event.target;
        const isEmojiPicker = target.closest('.emoji-picker-container');
        const isMentionDropdown = target.closest('.mention-dropdown-container');
        const isEmojiButton = target.closest('button[title="Emoji"]');
        const isTextarea = target.closest('textarea');

        if (!isEmojiPicker && !isEmojiButton && showEmojiPicker) {
          setShowEmojiPicker(false);
        }
        if (!isMentionDropdown && !isTextarea && showMentionDropdown) {
          setShowMentionDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker, showMentionDropdown]);

  // Update mention dropdown position on scroll and resize
  useEffect(() => {
    if (!showMentionDropdown || mentionStartPos === -1 || !textareaRef.current) {
      return;
    }

    const updatePosition = () => {
      if (textareaRef.current && mentionStartPos !== -1) {
        calculateMentionDropdownPosition(textareaRef.current, mentionStartPos);
      }
    };

    const textarea = textareaRef.current;
    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    textarea.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      textarea.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [showMentionDropdown, mentionStartPos, taskText]);

  // Check if mobile/tablet screen (max-width: 992px)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 992);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate header height for scrollable area
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [searchTerm]); // Recalculate when search term changes (affects header height)



  const isEmbedded = !!embeddedProjectId;

  return (
    <div className="container-fluid p-0 m-0" style={{
      overflowX: "hidden",
      overflowY: isEmbedded ? "hidden" : "auto",
      maxWidth: "100vw",
      width: "100%",
      paddingLeft: 0,
      paddingRight: 0,
      marginLeft: 0,
      marginRight: 0,
      backgroundColor: isEmbedded ? "#f9fafb" : "#f5f7fa",
      minHeight: isEmbedded ? "100%" : "100vh",
      height: isEmbedded ? "100%" : "auto",
      display: isEmbedded ? "flex" : "block",
      flexDirection: isEmbedded ? "column" : "initial",
      backgroundImage: isEmbedded ? "none" : "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4d4d4' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
    }}>
      {/* Header */}
      <div
        ref={headerRef}
        className="w-100 d-flex align-items-center justify-content-between flex-wrap py-2 py-md-3 task-header-responsive"
        style={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          gap: "10px",
          position: "sticky",
          top: 0,
          zIndex: 100,
          paddingLeft: isMobile ? "16px" : "24px",
          paddingRight: isMobile ? "16px" : "24px",
          marginLeft: 0,
          marginRight: 0,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
        }}
      >
        <div className="flex-grow-1" style={{ minWidth: "120px", display: "flex", flexDirection: "column", position: "relative" }}>
          {/* Desktop Back Button - Left Corner */}
          {!isMobile && (
            <button
              className="btn back-btn-responsive"
              style={{
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                padding: 0,
                fontSize: "18px",
                flexShrink: 0,
                position: "absolute",
                left: "0px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f3f4f6",
                border: "none",
                color: "#4b5563",
                transition: "all 0.2s ease"
              }}
              onClick={() => window.history.back()}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e5e7eb";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
              }}
            >
              ←
            </button>
          )}
          <div className="d-flex align-items-center justify-content-center">
            <h1 className="task-title-responsive mb-0" style={{ color: "#5b6770", fontWeight: 500, fontSize: isMobile ? "1.25rem" : "1.5rem" }}>JIS Project Chat</h1>
          </div>
          <div className="d-flex align-items-center" style={{ gap: "8px", justifyContent: "center", marginTop: "4px" }}>
            {/* Mobile Back Button - With Project Name */}
            {isMobile && (
              <button
                className="btn back-btn-responsive"
                style={{
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  fontSize: "16px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  color: "#4b5563"
                }}
                onClick={() => window.history.back()}
              >
                ←
              </button>
            )}
            <h2 className="task-subtitle-responsive mb-0" style={{ fontSize: "13px", color: "#64748b", fontWeight: 400 }}>
              {projectName}
            </h2>
          </div>
        </div>

        {/* Global Search Bar */}
        <div className="global-search-wrapper" style={{ flex: "0 1 auto", minWidth: isMobile ? "100%" : "200px", maxWidth: isMobile ? "100%" : "320px", width: "100%", display: "flex", flexDirection: "column" }}>
          <div className="input-group global-search-container" style={{ width: "100%", display: "flex", position: "relative", borderRadius: "8px", overflow: "hidden", border: "1px solid #e5e7eb", backgroundColor: "#ffffff" }}>
            <input
              type="text"
              className="form-control global-search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedIndex(-1);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && matchingTaskIndices.length > 0) {
                  e.preventDefault();
                  const firstIndex = matchingTaskIndices[0];
                  const taskId = tasks[firstIndex]?.id;
                  if (taskId && taskRefs.current[taskId]) {
                    taskRefs.current[taskId].scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setSelectedIndex(0);
                  }
                }
              }}
              style={{
                fontSize: "0.875rem",
                height: "36px",
                border: "none",
                paddingLeft: "12px",
                paddingRight: searchTerm ? "32px" : "12px",
                backgroundColor: "transparent",
                outline: "none"
              }}
            />
            {searchTerm && (
              <button
                className="btn btn-sm"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedIndex(-1);
                }}
                style={{
                  position: "absolute",
                  right: "40px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "18px",
                  padding: "0 4px",
                  zIndex: 10,
                  color: "#9ca3af",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                  e.currentTarget.style.color = "#4b5563";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#9ca3af";
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
            <span
              className="input-group-text bg-white"
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
                padding: "0 12px",
                border: "none",
                borderLeft: "1px solid #e5e7eb",
                color: "#64748b",
                transition: "color 0.2s ease"
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#5b6770"}
              onMouseLeave={(e) => e.currentTarget.style.color = "#64748b"}
            >
              <i className="bi bi-search"></i>
            </span>
          </div>
          {searchTerm && (
            <div style={{ paddingLeft: "4px", marginTop: "4px" }}>
              {matchingTaskIndices.length > 0 ? (
                <small style={{ fontSize: "0.75rem", color: "#64748b", display: "block" }}>
                  {matchingTaskIndices.length} result{matchingTaskIndices.length !== 1 ? 's' : ''} found
                  {selectedIndex >= 0 && ` • ${selectedIndex + 1}/${matchingTaskIndices.length}`}
                </small>
              ) : (
                <small style={{ fontSize: "0.75rem", color: "#9ca3af", display: "block" }}>
                  No results found
                </small>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Scrollable Timeline Container */}
      <div
        className="timeline-wrapper-responsive"
        style={{
          maxHeight: isEmbedded
            ? (isMobile ? "calc(100% - 140px)" : "calc(100% - 160px)")
            : (isMobile
              ? `calc(100vh - ${headerHeight}px - 80px)` // Mobile: Subtract header + WhatsApp input bar
              : `calc(100vh - ${headerHeight}px - 100px)`), // Desktop: Subtract header + WhatsApp input bar
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          paddingBottom: isMobile ? "90px" : "110px", // Add padding to prevent content from being hidden behind input bar
          flex: isEmbedded ? "1 1 auto" : "none",
          minHeight: isEmbedded ? 0 : "auto",
          WebkitOverflowScrolling: "touch"
        }}
      >
        <div className="timeline-container-responsive" style={{ paddingLeft: isMobile ? "12px" : "20px", paddingRight: isMobile ? "12px" : "20px", paddingTop: "24px", paddingBottom: "24px", backgroundColor: "transparent" }}>
          <Timeline
            position="right"
            className="task-timeline-responsive"
            sx={{
              paddingLeft: 0,
              paddingRight: 0,
              // Mobile: Change layout to vertical
              "@media (max-width: 767px)": {
                "& .MuiTimelineItem-root": {
                  flexDirection: "column",
                  alignItems: "flex-start",
                  "& .MuiTimelineOppositeContent-root": {
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: "100%",
                    paddingLeft: "48px",
                    paddingRight: "12px",
                    paddingBottom: "8px",
                    textAlign: "left",
                    order: 1
                  },
                  "& .MuiTimelineSeparator-root": {
                    order: 0,
                    position: "absolute",
                    left: 0
                  },
                  "& .MuiTimelineContent-root": {
                    width: "100%",
                    maxWidth: "100%",
                    paddingLeft: "48px",
                    order: 2
                  }
                }
              },
              // Desktop: Keep horizontal layout
              "@media (min-width: 768px)": {
                "& .MuiTimelineOppositeContent-root": {
                  minWidth: "120px",
                  maxWidth: "180px",
                  flexShrink: 0
                }
              }
            }}
          >
            {tasks.length === 0 ? (
              <div style={{ padding: "48px 24px", textAlign: "center" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}>💬</div>
                <p style={{ color: "#64748b", fontSize: "0.9375rem", marginBottom: "8px", fontWeight: 500 }}>
                  No messages yet
                </p>
                <p style={{ color: "#94a3b8", fontSize: "0.8125rem", margin: 0 }}>
                  Start a conversation by sending a message below
                </p>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div style={{ padding: "48px 24px", textAlign: "center" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}>🔍</div>
                <p style={{ color: "#64748b", fontSize: "0.9375rem", marginBottom: "8px", fontWeight: 500 }}>
                  No results found
                </p>
                <p style={{ color: "#94a3b8", fontSize: "0.8125rem", margin: 0 }}>
                  Try adjusting your search terms
                </p>
              </div>
            ) : (
              filteredTasks.map((t, index) => {
                const originalIndex = tasks.findIndex(task => task.id === t.id);
                const isSelected = selectedIndex >= 0 && matchingTaskIndices[selectedIndex] === originalIndex;
                // WhatsApp-style: Check if current user is the creator
                const isMyMessage = isTaskCreator(t);

                return (
                  <TimelineItem
                    key={t.id}
                    className={`task-item-responsive ${isMyMessage ? 'my-message' : 'other-message'}`}
                    sx={{
                      width: "100%",
                      position: "relative",
                      // WhatsApp-style alignment
                      justifyContent: isMyMessage ? "flex-end" : "flex-start",
                      // Mobile: Stack vertically
                      "@media (max-width: 767px)": {
                        flexDirection: "column",
                        alignItems: isMyMessage ? "flex-end" : "flex-start"
                      },
                      // Desktop: Keep horizontal
                      "@media (min-width: 768px)": {
                        flexDirection: "row"
                      }
                    }}
                  >

                    {/* Hidden DATE for cleaner WhatsApp-style look - timestamp shown in message card */}
                    <TimelineOppositeContent
                      sx={{
                        display: "none" // Hide for WhatsApp-style layout
                      }}
                    >
                      {formatTs(t.updatedAt)}
                    </TimelineOppositeContent>

                    {/* Hidden TimelineSeparator for cleaner WhatsApp-style look */}
                    <TimelineSeparator
                      sx={{
                        display: "none" // Hide timeline dots/connectors for WhatsApp-style chat
                      }}
                    >
                      <TimelineDot sx={{ display: "none" }} />
                      <TimelineConnector sx={{ display: "none" }} />
                    </TimelineSeparator>

                    {/* TASK CARD - WhatsApp-style alignment based on message ownership */}
                    <TimelineContent
                      sx={{
                        py: "4px",
                        px: 1,
                        display: "flex",
                        justifyContent: isMyMessage ? "flex-end" : "flex-start",
                        // Mobile: Full width below date
                        "@media (max-width: 767px)": {
                          width: "100%",
                          maxWidth: "100%",
                          paddingLeft: isMyMessage ? "8px" : "8px",
                          paddingRight: "8px",
                          paddingTop: "3px",
                          paddingBottom: "3px",
                          order: 2
                        },
                        // Desktop: Full width for better alignment
                        "@media (min-width: 768px)": {
                          flex: 1,
                          paddingLeft: "12px",
                          paddingRight: "12px",
                          paddingTop: "4px",
                          paddingBottom: "4px"
                        }
                      }}

                      className="timeline-content-responsive"
                    >
                      <Paper
                        elevation={0}
                        className={`card task-card-responsive ${isMyMessage ? 'my-message-card' : 'other-message-card'}`}
                        ref={(el) => {
                          if (el) taskRefs.current[t.id] = el;
                        }}
                        style={{
                          padding: "12px 16px",
                          borderRadius: isMyMessage ? "18px 4px 18px 18px" : "4px 18px 18px 18px",
                          // WhatsApp-style borders: right for my messages, left for others
                          borderLeft: isMyMessage ? "none" : "4px solid",
                          borderRight: isMyMessage ? "4px solid" : "none",
                          borderLeftColor: !isMyMessage ? (mentionedCardIds.includes(t.id)
                            ? "#fbbf24" // Amber for mentions
                            : (newChatIds.includes(t.id)
                              ? "#34d399" // Green for new chats
                              : (t.status === "Completed"
                                ? "#34d399"
                                : t.status === "Pending"
                                  ? "#fbbf24"
                                  : t.status === "In Progress"
                                    ? "#60a5fa"
                                    : t.status === "On Hold"
                                      ? "#f87171"
                                      : "#cbd5e1"))) : undefined,
                          borderRightColor: isMyMessage ? "#34d399" : undefined, // Green for my messages
                          cursor: "default",
                          maxWidth: isMobile ? "85%" : "68%",
                          width: "auto",
                          marginLeft: isMyMessage ? "auto" : "0",
                          marginRight: isMyMessage ? "0" : "auto",
                          marginBottom: "8px",
                          boxShadow: (isSelected || highlightedTaskId === t.id)
                            ? "0 4px 16px rgba(37, 99, 235, 0.2)"
                            : "0 2px 8px rgba(0, 0, 0, 0.06)",
                          border: (isSelected || highlightedTaskId === t.id)
                            ? "1px solid #2563eb"
                            : "1px solid transparent",
                          transition: "all 0.2s ease",
                          // WhatsApp-style background colors
                          backgroundColor: mentionedCardIds.includes(t.id)
                            ? "#fef3c7" // Yellow for mentions
                            : (newChatIds.includes(t.id)
                              ? "#d1fae5" // Green for new chats
                              : ((isSelected || highlightedTaskId === t.id)
                                ? "#dbeafe"
                                : (isMyMessage ? "#d1fae5" : "#ffffff"))), // Green tint for my messages, white for others
                          transform: highlightedTaskId === t.id ? "scale(1.02)" : "scale(1)",
                        }}
                        onClick={() => {
                          // Remove highlight when chat is clicked/viewed
                          if (newChatIds.includes(t.id)) {
                            setNewChatIds(prev => prev.filter(id => id !== t.id));
                            console.log(`✅ Removed highlight from chat ${t.id}`);
                          }
                        }}
                      >
                        <div className="d-flex align-items-start justify-content-between task-card-content" style={{ gap: "12px", flexDirection: "column" }}>

                          {/* QUOTED MESSAGE DISPLAY - If this message is a reply */}
                          {t.quotedMessage && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                if (t.quotedMessage?.id) {
                                  // Highlight the original message
                                  setHighlightedTaskId(t.quotedMessage.id);
                                  // Scroll to the original message
                                  setTimeout(() => {
                                    const originalMessageRef = taskRefs.current[t.quotedMessage.id];
                                    if (originalMessageRef) {
                                      originalMessageRef.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center'
                                      });
                                      // Remove highlight after 3 seconds
                                      setTimeout(() => {
                                        setHighlightedTaskId(null);
                                      }, 3000);
                                    }
                                  }, 100);
                                }
                              }}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                marginBottom: "8px",
                                backgroundColor: "#f3f4f6",
                                borderRadius: "8px",
                                borderLeft: "3px solid #0ea5e9",
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                transition: "all 0.2s ease"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#e5e7eb";
                                e.currentTarget.style.borderLeftColor = "#0284c7";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#f3f4f6";
                                e.currentTarget.style.borderLeftColor = "#0ea5e9";
                              }}
                              title="Click to view original message"
                            >
                              <div style={{ color: "#6b7280", fontWeight: 600, marginBottom: "4px" }}>
                                {capitalizeName(t.quotedMessage.createdByName || "Unknown")}
                              </div>
                              <div style={{ color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {t.quotedMessage.text || ""}
                              </div>
                            </div>
                          )}

                          <div className="d-flex align-items-start justify-content-between" style={{ width: "100%", gap: "12px" }}>
                            {/* LEFT — TEXT & STATUS */}
                            <div className="task-card-left" style={{ flex: "1 1 auto", minWidth: 0, maxWidth: "100%" }}>
                              {/* Metadata Section - Compact layout */}
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "6px", fontSize: "0.75rem", color: "#6b7280" }}>
                                <div>
                                  <span style={{ color: "#94a3b8" }}>By</span> <strong style={{ color: isTaskCreator(t) ? '#6ee7b7' : '#5b6770' }}>{highlightText(capitalizeName(t.createdByName || "Unknown"), searchTerm)}</strong>
                                </div>
                                {(t.type === "task" || !t.type) && t.updatedBy && (
                                  <>
                                    <span style={{ color: "#e5e7eb" }}>•</span>
                                    <div>
                                      <span style={{ color: "#9ca3af" }}>Updated by</span> <strong style={{ color: "#4b5563" }}>{highlightText(capitalizeName(t.updatedBy || "Unknown"), searchTerm)}</strong>
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Only show Updated By and Status for TASKS (not chats) */}
                              {(t.type === "task" || !t.type) && (
                                <>
                                  {/* Status and Assignment Row */}
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", marginBottom: "6px" }}>
                                    {/* Status Dropdown - Anyone can change */}
                                    <select
                                      value={t.status || "Pending"}
                                      onChange={(e) => updateTaskStatus(t.id, e.target.value)}
                                      style={{
                                        fontSize: "0.75rem",
                                        padding: "3px 8px",
                                        borderRadius: "4px",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        border: "1px solid #d1d5db",
                                        transition: "all 0.15s ease",
                                        backgroundColor:
                                          t.status === "Completed"
                                            ? "#ecfdf5"
                                            : t.status === "Pending"
                                              ? "#fffbeb"
                                              : t.status === "In Progress"
                                                ? "#eff6ff"
                                                : t.status === "On Hold"
                                                  ? "#fef2f2"
                                                  : "#f9fafb",
                                        color: t.status === "Completed"
                                          ? "#065f46"
                                          : t.status === "Pending"
                                            ? "#92400e"
                                            : t.status === "In Progress"
                                              ? "#1e40af"
                                              : t.status === "On Hold"
                                                ? "#991b1b"
                                                : "#4b5563",
                                        minWidth: "120px"
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = "#111827";
                                        e.target.style.boxShadow = "0 0 0 2px rgba(17, 24, 39, 0.1)";
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = "#d1d5db";
                                        e.target.style.boxShadow = "none";
                                      }}
                                    >
                                      <option value="Pending">Pending</option>
                                      <option value="In Progress">In Progress</option>
                                      <option value="On Hold">On Hold</option>
                                      <option value="Completed">Completed</option>
                                    </select>

                                    {/* Show Assigned To - Only for Pending tasks */}
                                    {t.status === "Pending" && (
                                      <select
                                        className="form-select form-select-sm"
                                        value={t.assignedTo || ""}
                                        onChange={(e) => updateTaskAssignment(t.id, e.target.value || null)}
                                        style={{
                                          fontSize: "0.8125rem",
                                          padding: "4px 10px",
                                          border: "1px solid #e5e7eb",
                                          borderRadius: "6px",
                                          backgroundColor: t.assignedTo ? "#d1fae5" : "#ffffff",
                                          color: "#5b6770",
                                          minWidth: "140px",
                                          cursor: "pointer"
                                        }}
                                      >
                                        <option value="">Unassigned</option>
                                        <option value={getCurrentUserUID()}>Myself</option>
                                        {projectMembers
                                          .filter(m => m.uid !== getCurrentUserUID())
                                          .map((member) => (
                                            <option key={member.uid} value={member.uid}>
                                              {capitalizeName(member.name)}
                                            </option>
                                          ))}
                                      </select>
                                    )}

                                    {/* Show Assigned To (Read-only) for non-Pending tasks */}
                                    {t.status !== "Pending" && t.assignedTo && (
                                      <div style={{ fontSize: "0.8125rem", color: "#6b7280" }}>
                                        <span style={{ color: "#9ca3af" }}>Assigned:</span> <strong style={{ color: "#4b5563" }}>{t.assignedToName || "Unknown"}</strong>
                                      </div>
                                    )}

                                    {/* Show Task Age - Skip for Completed tasks */}
                                    {t.status !== "Completed" && t.createdAt && (
                                      <div style={{ fontSize: "0.8125rem", color: "#9ca3af", fontStyle: "italic" }}>
                                        Age: <strong style={{ color: "#6b7280" }}>{calculateTaskAge(t.createdAt)}</strong>
                                      </div>
                                    )}
                                  </div>

                                  {/* Status Change History Log - Collapsible */}
                                  {t.statusHistory && Array.isArray(t.statusHistory) && t.statusHistory.length > 0 && (
                                    <details style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e5e7eb" }}>
                                      <summary style={{
                                        fontWeight: 500,
                                        color: "#5b6770",
                                        fontSize: "0.8125rem",
                                        cursor: "pointer",
                                        listStyle: "none",
                                        userSelect: "none"
                                      }}>
                                        <span style={{ color: "#6b7280" }}>Status History ({t.statusHistory.length})</span>
                                      </summary>
                                      <div style={{ marginTop: "6px", maxHeight: "120px", overflowY: "auto", fontSize: "0.75rem", lineHeight: "1.6", paddingLeft: "8px" }}>
                                        {t.statusHistory
                                          .slice()
                                          .reverse()
                                          .map((entry, idx) => (
                                            <div key={idx} style={{ marginBottom: "6px", paddingBottom: "6px", borderBottom: idx < t.statusHistory.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                                              <div style={{ color: "#6b7280", marginBottom: "2px" }}>
                                                {formatTs(entry.updatedAt)}
                                              </div>
                                              <div style={{ color: "#4b5563" }}>
                                                Changed to <strong>{entry.status}</strong> by <strong>{entry.updatedBy || "Unknown"}</strong>
                                                {entry.previousStatus && entry.previousStatus !== entry.status && (
                                                  <span style={{ color: "#9ca3af" }}> • from {entry.previousStatus}</span>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </details>
                                  )}
                                </>
                              )}

                              {/* Task/Chat Description */}
                              <div className="task-text" style={{
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap", // Preserve line breaks and spacing
                                fontSize: "0.875rem",
                                lineHeight: "1.5",
                                color: "#5b6770",
                                marginTop: "6px",
                                padding: "6px 0 2px 0",
                                borderTop: "1px solid #f3f4f6"
                              }}>
                                {/* Use renderChatText to show @mentions in blue for both tasks and chats */}
                                {renderChatText(t.text || "")}
                              </div>

                              {/* WhatsApp-style timestamp at bottom-right of message */}
                              <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: "2px"
                              }}>
                                <span style={{
                                  fontSize: "0.6875rem",
                                  color: "#9ca3af",
                                  fontWeight: 400,
                                  letterSpacing: "0.01em"
                                }}>
                                  {formatTs(t.updatedAt)}
                                </span>
                              </div>

                              {/* QUOTE REPLY AND THUMBS UP BUTTONS */}
                              <div style={{
                                marginTop: "8px",
                                paddingTop: "8px",
                                borderTop: "1px solid #f3f4f6",
                                display: "flex",
                                gap: "8px",
                                alignItems: "center"
                              }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuoteReply(t);
                                  }}
                                  style={{
                                    padding: isMobile ? "10px 14px" : "6px 12px",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    borderRadius: "20px",
                                    border: "1px solid #e5e7eb",
                                    backgroundColor: "#ffffff",
                                    color: "#6b7280",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    minHeight: isMobile ? "44px" : "auto",
                                    minWidth: isMobile ? "44px" : "auto"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                                    e.currentTarget.style.borderColor = "#9ca3af";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#ffffff";
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                  }}
                                >
                                  <i className="bi bi-reply" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
                                  {!isMobile && <span>Quote Reply</span>}
                                </button>

                                {/* Thumbs Up Reaction Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleThumbsUpReaction(t.id, t.thumbsUpReactions || []);
                                  }}
                                  style={{
                                    padding: isMobile ? "10px 14px" : "6px 12px",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    borderRadius: "20px",
                                    border: (t.thumbsUpReactions || []).length > 0
                                      ? "1px solid #e5e7eb"
                                      : "1px solid #e5e7eb",
                                    backgroundColor: (t.thumbsUpReactions || []).find(
                                      (r) => r.email === getCurrentUserEmail()
                                    ) ? "#dbeafe"
                                      : (t.thumbsUpReactions || []).length > 0
                                        ? "#f9fafb"
                                        : "#ffffff",
                                    color: (t.thumbsUpReactions || []).find(
                                      (r) => r.email === getCurrentUserEmail()
                                    ) ? "#2563eb"
                                      : (t.thumbsUpReactions || []).length > 0
                                        ? "#6b7280"
                                        : "#9ca3af",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    position: "relative",
                                    opacity: (t.thumbsUpReactions || []).length > 0 ? 1 : 0.6,
                                    minHeight: isMobile ? "44px" : "auto",
                                    minWidth: isMobile ? "44px" : "auto"
                                  }}
                                  onMouseEnter={(e) => {
                                    const hasReactions = (t.thumbsUpReactions || []).length > 0;
                                    const userReacted = (t.thumbsUpReactions || []).find(
                                      (r) => r.email === getCurrentUserEmail()
                                    );
                                    e.currentTarget.style.backgroundColor = userReacted
                                      ? "#bfdbfe"
                                      : hasReactions
                                        ? "#f3f4f6"
                                        : "#f9fafb";
                                    e.currentTarget.style.borderColor = "#9ca3af";
                                    e.currentTarget.style.opacity = "1";
                                  }}
                                  onMouseLeave={(e) => {
                                    const hasReactions = (t.thumbsUpReactions || []).length > 0;
                                    const userReacted = (t.thumbsUpReactions || []).find(
                                      (r) => r.email === getCurrentUserEmail()
                                    );
                                    e.currentTarget.style.backgroundColor = userReacted
                                      ? "#dbeafe"
                                      : hasReactions
                                        ? "#f9fafb"
                                        : "#ffffff";
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                    e.currentTarget.style.opacity = hasReactions ? "1" : "0.6";
                                  }}
                                  title={
                                    (t.thumbsUpReactions || []).length > 0
                                      ? `Reacted by: ${(t.thumbsUpReactions || [])
                                        .map((r) => capitalizeName(r.name || r.email?.split("@")[0] || "Unknown"))
                                        .join(", ")}`
                                      : "Thumbs up"
                                  }
                                >
                                  <span style={{
                                    fontSize: "16px",
                                    opacity: (t.thumbsUpReactions || []).length > 0 ? 1 : 0.7
                                  }}>👍</span>
                                  {(t.thumbsUpReactions || []).length > 0 && (
                                    <span style={{
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      color: "inherit"
                                    }}>
                                      {(t.thumbsUpReactions || []).length}
                                    </span>
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* RIGHT — ACTION BUTTONS - Only show if user is creator */}
                            {isTaskCreator(t) && (
                              <div className="d-flex gap-1 task-action-buttons" style={{ flexShrink: 0, alignSelf: "flex-start", marginTop: "4px" }}>
                                {/* EDIT ICON */}
                                <button
                                  className="btn btn-sm d-flex align-items-center justify-content-center task-action-btn"
                                  onClick={() => {
                                    if (!isTaskCreator(t)) {
                                      alert("You don't have permission to edit this task. Only the creator can edit.");
                                      return;
                                    }
                                    openEditModal(t);
                                  }}
                                  style={{
                                    borderRadius: "6px",
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #e5e7eb",
                                    backgroundColor: "#ffffff",
                                    color: "#2563eb",
                                    transition: "all 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#eff6ff";
                                    e.currentTarget.style.borderColor = "#2563eb";
                                    e.currentTarget.style.transform = "scale(1.05)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#ffffff";
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                    e.currentTarget.style.transform = "scale(1)";
                                  }}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil" style={{ fontSize: "14px" }}></i>
                                </button>
                                {/* DELETE ICON */}
                                <button
                                  className="btn btn-sm d-flex align-items-center justify-content-center task-action-btn"
                                  onClick={() => {
                                    if (!isTaskCreator(t)) {
                                      alert("You don't have permission to delete this task. Only the creator can delete.");
                                      return;
                                    }
                                    confirmDelete(t.id);
                                  }}
                                  style={{
                                    borderRadius: "6px",
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #e5e7eb",
                                    backgroundColor: "#ffffff",
                                    color: "#ef4444",
                                    transition: "all 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#fef2f2";
                                    e.currentTarget.style.borderColor = "#ef4444";
                                    e.currentTarget.style.transform = "scale(1.05)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#ffffff";
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                    e.currentTarget.style.transform = "scale(1)";
                                  }}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash" style={{ fontSize: "14px" }}></i>
                                </button>
                              </div>
                            )}
                          </div>

                        </div>
                      </Paper>
                    </TimelineContent>

                  </TimelineItem>
                );
              })
            )}
          </Timeline>
        </div>
      </div>

      {/* WhatsApp-style input bar for both mobile and desktop */}
      <div
        className="whatsapp-input-container"
        style={{
          position: isEmbedded ? "sticky" : "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#ffffff",
          padding: isMobile ? "12px 14px" : "14px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          alignItems: "stretch",
          zIndex: 100,
          borderTop: "1px solid #e5e7eb",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
          flexShrink: 0
        }}
      >
        {/* Mode Toggle: Chat/Task and Assign To dropdown - Only show on desktop */}
        {!isMobile && (
          <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "2px", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setIsCheckboxChecked(true)}
              style={{
                padding: "6px 14px",
                borderRadius: "8px",
                border: "none",
                fontSize: "0.8125rem",
                fontWeight: 500,
                cursor: "pointer",
                backgroundColor: isCheckboxChecked ? "#9ca3af" : "#ffffff",
                color: isCheckboxChecked ? "#ffffff" : "#6b7280",
                transition: "all 0.15s ease",
                boxShadow: isCheckboxChecked ? "0 1px 3px rgba(0,0,0,0.1)" : "0 1px 2px rgba(0,0,0,0.05)",
                ...(isCheckboxChecked ? {} : { border: "1px solid #e5e7eb" })
              }}
              onMouseEnter={(e) => {
                if (!isCheckboxChecked) {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                }
              }}
              onMouseLeave={(e) => {
                if (!isCheckboxChecked) {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                }
              }}
            >
              Chat
            </button>
            <button
              type="button"
              onClick={() => setIsCheckboxChecked(false)}
              style={{
                padding: "6px 14px",
                borderRadius: "8px",
                border: "none",
                fontSize: "0.8125rem",
                fontWeight: 500,
                cursor: "pointer",
                backgroundColor: !isCheckboxChecked ? "#9ca3af" : "#ffffff",
                color: !isCheckboxChecked ? "#ffffff" : "#6b7280",
                transition: "all 0.15s ease",
                boxShadow: !isCheckboxChecked ? "0 1px 3px rgba(0,0,0,0.1)" : "0 1px 2px rgba(0,0,0,0.05)",
                ...(!isCheckboxChecked ? {} : { border: "1px solid #e5e7eb" })
              }}
              onMouseEnter={(e) => {
                if (isCheckboxChecked) {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                }
              }}
              onMouseLeave={(e) => {
                if (isCheckboxChecked) {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                }
              }}
            >
              Task
            </button>

            {/* Assign To dropdown - Show next to Task button when project members exist AND in Task mode */}
            {!isCheckboxChecked && projectMembers.length > 0 && (
              <select
                className="form-select form-select-sm"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                disabled={isCheckboxChecked}
                style={{
                  fontSize: "0.8125rem",
                  padding: "6px 12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  backgroundColor: isCheckboxChecked ? "#f9fafb" : "white",
                  color: isCheckboxChecked ? "#9ca3af" : "#374151",
                  cursor: isCheckboxChecked ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  opacity: isCheckboxChecked ? 0.6 : 1
                }}
                onFocus={(e) => {
                  if (!isCheckboxChecked) {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="">👤 Unassigned</option>
                <option value={getCurrentUserUID()}>👤 Myself ({auth.currentUser?.displayName || auth.currentUser?.email || "Me"})</option>
                {projectMembers
                  .filter(m => m.uid !== getCurrentUserUID())
                  .map((member) => (
                    <option key={member.uid} value={member.uid}>
                      👤 {capitalizeName(member.name)}
                    </option>
                  ))}
              </select>
            )}
          </div>
        )}

        {/* Quoted Message Preview */}
        {quotedMessage && (
          <div style={{
            padding: "8px 12px",
            backgroundColor: "#f3f4f6",
            borderRadius: "8px",
            borderLeft: "3px solid #0ea5e9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "8px",
            marginBottom: "4px"
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#6b7280", fontWeight: 600, fontSize: "0.75rem", marginBottom: "4px" }}>
                {capitalizeName(quotedMessage.createdByName || "Unknown")}
              </div>
              <div style={{ color: "#9ca3af", fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {quotedMessage.text || ""}
              </div>
            </div>
            <button
              onClick={() => setQuotedMessage(null)}
              style={{
                padding: "4px",
                border: "none",
                backgroundColor: "transparent",
                color: "#9ca3af",
                cursor: "pointer",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e5e7eb";
                e.currentTarget.style.color = "#6b7280";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#9ca3af";
              }}
              title="Remove quote"
            >
              <i className="bi bi-x" style={{ fontSize: "16px" }}></i>
            </button>
          </div>
        )}

        {/* Input and Action Buttons Row */}
        <div style={{ display: "flex", gap: "8px", alignItems: "center", position: "relative", backgroundColor: "#ffffff", borderRadius: "24px", padding: "6px 6px 6px 16px", border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", transition: "all 0.2s ease" }}>
          {/* Plus Icon Button - Toggle Chat/Task on mobile, show options on desktop */}
          <button
            type="button"
            onClick={() => {
              if (isMobile) {
                setIsCheckboxChecked(!isCheckboxChecked);
              }
            }}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              border: "none",
              backgroundColor: "transparent",
              color: "#54656f",
              cursor: "pointer",
              flexShrink: 0,
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f0f2f5";
              e.currentTarget.style.transform = "rotate(90deg)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.transform = "rotate(0deg)";
            }}
            title={isMobile ? (isCheckboxChecked ? "Switch to Task" : "Switch to Chat") : "Options"}
          >
            <i className="bi bi-plus-lg" style={{ fontSize: "22px" }}></i>
          </button>


          {/* Input Field - Multiline Textarea */}
          <textarea
            ref={textareaRef}
            value={taskText}
            onChange={(e) => {
              const newText = e.target.value;
              const cursorPos = e.target.selectionStart;
              setTaskText(newText);

              // Check if @ was just typed or if we're in a mention context
              if (isCheckboxChecked && projectMembers.length > 0) {
                // Look for @ symbol before cursor
                const textBeforeCursor = newText.substring(0, cursorPos);
                const lastAtIndex = textBeforeCursor.lastIndexOf('@');

                if (lastAtIndex !== -1) {
                  // Check if there's a space or newline after @ (meaning mention ended)
                  const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
                  const hasSpaceOrNewline = /[\s\n]/.test(textAfterAt);

                  if (!hasSpaceOrNewline) {
                    // We're in a mention - extract query and show dropdown
                    const query = textAfterAt;
                    setMentionQuery(query);
                    setMentionStartPos(lastAtIndex);
                    setShowMentionDropdown(true);
                    setShowEmojiPicker(false);
                    setSelectedMentionIndex(0);

                    // Calculate position for dropdown next to @
                    setTimeout(() => {
                      calculateMentionDropdownPosition(e.target, lastAtIndex);
                    }, 0);
                  } else {
                    // Mention ended (space after @)
                    setShowMentionDropdown(false);
                    setMentionQuery("");
                    setMentionStartPos(-1);
                  }
                } else {
                  // No @ found
                  setShowMentionDropdown(false);
                  setMentionQuery("");
                  setMentionStartPos(-1);
                }
              }
            }}
            onKeyDown={(e) => {
              // Handle mention dropdown navigation
              if (showMentionDropdown && isCheckboxChecked) {
                const filteredMembers = getFilteredMembers();

                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSelectedMentionIndex(prev => {
                    const newIndex = prev < filteredMembers.length - 1 ? prev + 1 : 0;
                    // Scroll selected item into view and update position
                    setTimeout(() => {
                      const itemRef = mentionItemRefs.current[newIndex];
                      if (itemRef && mentionDropdownRef.current) {
                        itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                      }
                      // Recalculate position in case textarea scrolled
                      if (textareaRef.current && mentionStartPos !== -1) {
                        calculateMentionDropdownPosition(textareaRef.current, mentionStartPos);
                      }
                    }, 0);
                    return newIndex;
                  });
                  return;
                }

                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSelectedMentionIndex(prev => {
                    const newIndex = prev > 0 ? prev - 1 : filteredMembers.length - 1;
                    // Scroll selected item into view and update position
                    setTimeout(() => {
                      const itemRef = mentionItemRefs.current[newIndex];
                      if (itemRef && mentionDropdownRef.current) {
                        itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                      }
                      // Recalculate position in case textarea scrolled
                      if (textareaRef.current && mentionStartPos !== -1) {
                        calculateMentionDropdownPosition(textareaRef.current, mentionStartPos);
                      }
                    }, 0);
                    return newIndex;
                  });
                  return;
                }

                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  if (filteredMembers.length > 0) {
                    const selectedMember = filteredMembers[selectedMentionIndex];
                    if (selectedMember) {
                      insertMention(selectedMember.name);
                      // Focus back on textarea
                      setTimeout(() => {
                        const textarea = e.target;
                        textarea.focus();
                        const newPos = mentionStartPos + selectedMember.name.length + 2; // @ + name + space
                        textarea.setSelectionRange(newPos, newPos);
                      }, 0);
                    }
                  }
                  return;
                }

                if (e.key === 'Escape') {
                  e.preventDefault();
                  setShowMentionDropdown(false);
                  setMentionQuery("");
                  setMentionStartPos(-1);
                  return;
                }
              }

              // Send message on Enter (without Shift)
              if (e.key === 'Enter' && !e.shiftKey && taskText.trim() && !showMentionDropdown) {
                e.preventDefault(); // Prevent default newline
                addTask();
                setIsCheckboxChecked(true); // Reset to true (checked) after adding
                setAssignedTo(""); // Reset assignment
              }
              // Shift + Enter allows new line (default behavior)
            }}
            className="form-control whatsapp-input-field"
            placeholder={isCheckboxChecked ? "Type a message..." : "Type a task..."}
            rows={1}
            style={{
              flex: 1,
              border: "none",
              padding: "10px 12px",
              fontSize: "0.9375rem",
              backgroundColor: "transparent",
              outline: "none",
              color: "#1f2937",
              minHeight: "40px",
              maxHeight: "120px",
              lineHeight: "1.5",
              fontWeight: 400,
              resize: "none",
              overflowY: "auto",
              fontFamily: "inherit"
            }}
            onInput={(e) => {
              // Auto-resize textarea based on content
              e.target.style.height = "auto";
              const newHeight = Math.min(e.target.scrollHeight, 120); // Max 120px
              e.target.style.height = `${newHeight}px`;
            }}
          />

          {/* Emoji/Smile Icon - Only show for chats */}
          {isCheckboxChecked && (
            <button
              type="button"
              onClick={() => {
                setShowEmojiPicker(!showEmojiPicker);
                setShowMentionDropdown(false); // Close mention dropdown if open
              }}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                border: "none",
                backgroundColor: showEmojiPicker ? "#f3f4f6" : "transparent",
                color: showEmojiPicker ? "#4b5563" : "#6b7280",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if (!showEmojiPicker) {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                }
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                if (!showEmojiPicker) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
                e.currentTarget.style.transform = "scale(1)";
              }}
              title="Emoji"
            >
              <i className="bi bi-emoji-smile" style={{ fontSize: "20px" }}></i>
            </button>
          )}

          {/* Send Button - Enhanced styling */}
          <button
            onClick={() => {
              addTask();
              setIsCheckboxChecked(true); // Reset to true (checked) after adding
              setAssignedTo(""); // Reset assignment
            }}
            disabled={!taskText.trim()}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              backgroundColor: taskText.trim() ? "#34d399" : "#d1d5db",
              border: "none",
              cursor: taskText.trim() ? "pointer" : "not-allowed",
              flexShrink: 0,
              transition: "all 0.2s ease",
              boxShadow: taskText.trim() ? "0 2px 8px rgba(52, 211, 153, 0.3)" : "none"
            }}
            onMouseEnter={(e) => {
              if (taskText.trim()) {
                e.currentTarget.style.backgroundColor = "#10b981";
                e.currentTarget.style.transform = "scale(1.08)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(52, 211, 153, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (taskText.trim()) {
                e.currentTarget.style.backgroundColor = "#34d399";
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(52, 211, 153, 0.3)";
              }
            }}
            title="Send"
          >
            <i className="bi bi-send-fill" style={{ fontSize: "18px", color: "white" }}></i>
          </button>
        </div>

        {/* Emoji Picker - WhatsApp style */}
        {showEmojiPicker && isCheckboxChecked && (
          <div
            className="emoji-picker-container"
            style={{
              position: "absolute",
              bottom: "100%",
              left: isMobile ? "12px" : "16px",
              right: isMobile ? "12px" : "16px",
              marginBottom: "10px",
              backgroundColor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              maxHeight: "280px",
              overflowY: "auto",
              zIndex: 1002,
              padding: "12px"
            }}
          >
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(6, 1fr)" : "repeat(8, 1fr)",
              gap: "4px",
              maxWidth: "100%"
            }}>
              {commonEmojis.map((emoji, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => insertEmoji(emoji)}
                  style={{
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    border: "none",
                    backgroundColor: "transparent",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    padding: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f2f5";
                    e.currentTarget.style.transform = "scale(1.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mention Dropdown - Show when @ is typed or button is clicked */}
        {showMentionDropdown && isCheckboxChecked && projectMembers.length > 0 && (
          <div
            ref={mentionDropdownRef}
            className="mention-dropdown-container"
            style={{
              position: "fixed",
              top: `${mentionDropdownPosition.top}px`,
              left: `${mentionDropdownPosition.left}px`,
              marginBottom: "4px",
              backgroundColor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              maxHeight: "280px",
              overflowY: "auto",
              zIndex: 1002,
              minWidth: "240px",
              maxWidth: "90vw",
              padding: "4px",
              transform: "translateY(-100%)"
            }}
          >
            <div style={{ padding: "8px 12px", fontSize: "0.75rem", color: "#9ca3af", fontWeight: 500, borderBottom: "1px solid #f3f4f6", marginBottom: "4px" }}>
              {mentionQuery ? `Mention someone matching "${mentionQuery}"` : "Mention someone"}
            </div>
            {getFilteredMembers().length > 0 ? (
              getFilteredMembers().map((member, idx) => (
                <div
                  key={member.uid}
                  ref={el => mentionItemRefs.current[idx] = el}
                  onClick={() => insertMention(member.name)}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    borderBottom: idx < getFilteredMembers().length - 1 ? "1px solid #f3f4f6" : "none",
                    transition: "background-color 0.15s ease",
                    borderRadius: "8px",
                    margin: "2px 0",
                    backgroundColor: idx === selectedMentionIndex ? "#f0f9ff" : "transparent",
                    borderLeft: idx === selectedMentionIndex ? "3px solid #0ea5e9" : "3px solid transparent"
                  }}
                  onMouseEnter={() => setSelectedMentionIndex(idx)}
                  onMouseLeave={() => { }}
                >
                  <div style={{ fontWeight: "500", fontSize: "0.9375rem", color: "#111827", marginBottom: "2px" }}>
                    {capitalizeName(member.name)}
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "#6b7280" }}>{member.email}</div>
                </div>
              ))
            ) : (
              <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "0.875rem" }}>
                No members found matching "{mentionQuery}"
              </div>
            )}
          </div>
        )}
      </div>


      {/* ---------- Edit Modal ---------- */}
      {showEditModal && editingTask && (
        <div className="modal show d-block modal-overlay-responsive" tabIndex="-1" role="dialog" style={{ padding: "15px" }}>
          <div className="modal-dialog modal-dialog-centered task-modal-responsive" role="document" style={{ maxWidth: "500px", width: "100%" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title modal-title-responsive">
                  {editingTask.type === "chat" ? "Edit Chat" : "Edit Task"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTask(null);
                  }}
                />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Task Text</label>
                  <input
                    className="form-control modal-input-responsive"
                    value={editingTask.text}
                    onChange={(e) =>
                      setEditingTask({ ...editingTask, text: e.target.value })
                    }
                    readOnly={!editingTask.isCreator}
                    style={{
                      backgroundColor: !editingTask.isCreator ? "#f9fafb" : "white",
                      cursor: !editingTask.isCreator ? "not-allowed" : "text"
                    }}
                  />
                  {!editingTask.isCreator && (
                    <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Only the task creator can edit the text
                    </small>
                  )}
                </div>

                {/* Status Dropdown - Only for tasks (not chats) */}
                {(editingTask.type === "task" || !editingTask.type) && (
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={editingTask.status || "Pending"}
                      onChange={(e) =>
                        setEditingTask({ ...editingTask, status: e.target.value })
                      }
                      style={{ fontSize: "0.9rem" }}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                    </select>
                    <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Anyone can update the task status
                    </small>
                  </div>
                )}

              </div>

              <div className="modal-footer d-flex flex-column flex-sm-row justify-content-end gap-2">

                <button className="btn btn-success modal-btn-responsive" onClick={saveEdit}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Delete Confirmation Modal ---------- */}
      {showDeleteConfirm && (
        <div className="modal show d-block modal-overlay-responsive" tabIndex="-1" role="dialog" style={{ padding: "15px" }}>
          <div className="modal-dialog modal-sm modal-dialog-centered task-modal-responsive" role="document" style={{ maxWidth: "400px", width: "100%" }}>
            <div className="modal-content">
              <div className="modal-body">
                <p className="delete-confirm-text">Are you sure you want to delete this task?</p>
                <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
                  <button
                    className="btn btn-secondary modal-btn-responsive"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger modal-btn-responsive" onClick={doDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}
